import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export const OPEN_SUPPORT_CHAT_EVENT = 'open-support-chat';

interface SupportChatVisibilityContextValue {
  isOpen: boolean;
  isMinimized: boolean;
  openChat: () => void;
  closeChat: () => void;
  minimizeChat: () => void;
  toggleChat: () => void;
}

const SupportChatVisibilityContext = createContext<SupportChatVisibilityContextValue | null>(null);

export function useSupportChatVisibility() {
  const context = useContext(SupportChatVisibilityContext);
  if (!context) {
    throw new Error('useSupportChatVisibility must be used within SupportChatVisibilityProvider');
  }
  return context;
}

export function useSupportChatVisibilityOptional() {
  return useContext(SupportChatVisibilityContext);
}

interface SupportChatVisibilityProviderProps {
  children: React.ReactNode;
}

export function SupportChatVisibilityProvider({ children }: SupportChatVisibilityProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const openChat = useCallback(() => {
    setIsOpen(true);
    setIsMinimized(false);
  }, []);

  useEffect(() => {
    window.addEventListener(OPEN_SUPPORT_CHAT_EVENT, openChat);
    return () => window.removeEventListener(OPEN_SUPPORT_CHAT_EVENT, openChat);
  }, [openChat]);

  const closeChat = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(false);
  }, []);

  const minimizeChat = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const toggleChat = useCallback(() => {
    if (isOpen && !isMinimized) {
      setIsMinimized(true);
    } else {
      setIsOpen(true);
      setIsMinimized(false);
    }
  }, [isOpen, isMinimized]);

  const value: SupportChatVisibilityContextValue = {
    isOpen,
    isMinimized,
    openChat,
    closeChat,
    minimizeChat,
    toggleChat,
  };

  return (
    <SupportChatVisibilityContext.Provider value={value}>
      {children}
    </SupportChatVisibilityContext.Provider>
  );
}
